import React, { useRef, memo, useMemo, useState, useEffect } from 'react';
import WordCloudLogo from './components/WordCloudLogo';
import WordCloudPlaceholder from './components/WordCloudPlaceholder';
import WordCloudExplanation from './components/WordCloudExplanation';

/**
 * Main WordCloud component.
 * Renders the server-generated character cloud SVG image.
 */
const WordCloudComponent = memo(
    ({ words, firstName, imageUrl, updatedAt, renderedAt }) => {
        const containerRef = useRef(null);
        const [hasImageError, setHasImageError] = useState(false);

        // Reset the error state whenever a newly rendered image becomes available, so a
        // placeholder shown while the first cloud was still generating is replaced once it
        // exists.
        const version = renderedAt || updatedAt;
        useEffect(() => {
            setHasImageError(false);
        }, [version]);

        const hasWords = Array.isArray(words) && words.length > 0;

        // Signed S3 URLs change on every fetch (new signature), so swapping the <img> src on
        // each poll would reload the same picture and flicker. We only recompute the src when
        // the content version changes (a newer cloud was rendered, or no version is available),
        // keeping the displayed image stable between polls.
        const lastVersionRef = useRef(null);
        const lastSrcRef = useRef(null);
        const imageSrc = useMemo(() => {
            if (!imageUrl) {
                lastVersionRef.current = null;
                lastSrcRef.current = null;
                return null;
            }

            if (version && lastSrcRef.current && lastVersionRef.current === version) {
                return lastSrcRef.current;
            }

            let src;
            try {
                const url = new URL(imageUrl);
                const params = url.searchParams;
                const isSignedUrl = params.has('X-Amz-Signature') || params.has('Signature');
                if (isSignedUrl) {
                    src = imageUrl;
                } else {
                    if (version) url.searchParams.set('v', version);
                    src = url.toString();
                }
            } catch {
                src = version
                    ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`
                    : imageUrl;
            }

            lastVersionRef.current = version;
            lastSrcRef.current = src;
            return src;
        }, [imageUrl, version]);

        // Early return for empty state
        if (!hasWords) {
            return (
                <div className="word-cloud-container" ref={containerRef}>
                    <WordCloudLogo />
                    <WordCloudPlaceholder />
                    <WordCloudExplanation firstName={firstName} />
                </div>
            );
        }

        if (!imageSrc || hasImageError) {
            return (
                <div className="word-cloud-container" ref={containerRef}>
                    <div className="word-cloud-wrapper-inner word-cloud-placeholder">
                        <div className="word-cloud-empty">Character cloud image is being generated.</div>
                    </div>
                    <WordCloudExplanation firstName={firstName} />
                </div>
            );
        }

        return (
            <div className="word-cloud-container" ref={containerRef}>
                <WordCloudLogo />
                <div className="word-cloud-wrapper-inner">
                    <img
                        className="word-cloud-image"
                        src={imageSrc}
                        alt={`Character cloud for ${firstName}`}
                        onError={() => setHasImageError(true)}
                    />
                </div>
                <WordCloudExplanation firstName={firstName} />
            </div>
        );
    },
    (prevProps, nextProps) => {
        try {
            // Re-render when the rendered image changes (a newer cloud finished generating)
            // or the image URL is refreshed, even if the words themselves are unchanged.
            if (
                prevProps.renderedAt !== nextProps.renderedAt ||
                prevProps.imageUrl !== nextProps.imageUrl ||
                prevProps.updatedAt !== nextProps.updatedAt
            ) {
                return false; // Different, re-render
            }

            // Custom comparison: only re-render if words array actually changed
            if (prevProps.words === nextProps.words) {
                return true; // Props are equal, skip re-render
            }

            // If both are empty, they're equal
            if (
                (!prevProps.words || prevProps.words.length === 0) &&
                (!nextProps.words || nextProps.words.length === 0)
            ) {
                return true; // Both empty, skip re-render
            }

            // If lengths differ, they're different
            if ((prevProps.words?.length || 0) !== (nextProps.words?.length || 0)) {
                return false; // Different, re-render
            }

            // Compare word content safely
            const prevWords = (prevProps.words || [])
                .map(w => {
                    if (!w || typeof w !== 'object') return '';
                    return `${w.text || w.word || w.adjective || ''}:${w.value || w.count || w.frequency || 0}`;
                })
                .filter(Boolean)
                .sort()
                .join('|');
            const nextWords = (nextProps.words || [])
                .map(w => {
                    if (!w || typeof w !== 'object') return '';
                    return `${w.text || w.word || w.adjective || ''}:${w.value || w.count || w.frequency || 0}`;
                })
                .filter(Boolean)
                .sort()
                .join('|');

            return prevWords === nextWords; // true = skip re-render, false = re-render
        } catch (error) {
            // If comparison fails, allow re-render to be safe
            console.warn('[WordCloud] Memo comparison error:', error);
            return false; // Re-render on error
        }
    },
);

WordCloudComponent.displayName = 'WordCloudComponent';

export default WordCloudComponent;
